import { Box } from 'designSystem/components/Box'
import type { DocumentTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsTableView/DocumentTableColumns'
import { FadeInMount } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/search/docChatStyles'
import { useDocumentSearchData } from 'features/knowledgeDocumentViewer/components/advancedSearch/contexts/DocumentSearchDataContext'
import {
  collectDatapointValueCitations,
  datapointValueCitationKey,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/datapointValueCitations'
import type {
  ResponseSegment,
  TimeSeriesComputedSeries,
  TimeSeriesSeriesSummary,
  TimeSeriesTrendRef,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import { groupPageTextCitations } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/groupPageTextCitations'
import {
  collectInspectionAnalysisCitations,
  inspectionAnalysisCitationKey,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/inspectionAnalysisCitations'
import { resolveCitationDocument } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/resolveCitationDocument'
import type { PreviewTrendHandler } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/trendPreview'
import { usePipelineConfig } from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import { unscopeSearchDocumentIdForHydration } from 'features/knowledgeDocumentViewer/utils/pipelineConfigScopedIds'
import React from 'react'

import type { PreviewDocumentHandler } from './previewDocumentHandler'
import { ResponseSegmentCitationChip } from './ResponseSegmentCitationChip'
import { ResponseSegmentCmmsChip } from './ResponseSegmentCmmsChip'
import { ResponseSegmentInspectionChip } from './ResponseSegmentInspectionChip'
import { ResponseSegmentValueChip } from './ResponseSegmentValueChip'

export function ResponseSegmentCitations({
  citations,
  citationLookup,
  timeSeriesSummaries,
  timeSeriesCitedSeries,
  trendRef,
  suppressedValueKeys,
  onCitationHoverChange,
  onPreviewDocument,
  onPreviewTrend,
}: {
  citations: ResponseSegment['citations']
  citationLookup: Map<string, DocumentTableRow>
  timeSeriesSummaries?: Map<string, TimeSeriesSeriesSummary>
  timeSeriesCitedSeries?: Map<string, TimeSeriesComputedSeries>
  trendRef?: TimeSeriesTrendRef
  suppressedValueKeys?: ReadonlySet<string>
  onCitationHoverChange?: (highlighted: boolean) => void
  onPreviewDocument?: PreviewDocumentHandler
  onPreviewTrend?: PreviewTrendHandler
}): React.ReactElement | null {
  const { documentResults } = useDocumentSearchData()
  const { selectedPipelineConfigId } = usePipelineConfig()

  const groupedCitations = groupPageTextCitations(citations)
  const valueCitations = collectDatapointValueCitations(citations).filter(
    (citation) => !suppressedValueKeys?.has(datapointValueCitationKey(citation))
  )

  const cmmsCitations = citations.filter(
    (citation) => citation.type === 'cmms_record'
  )
  const inspectionCitations = collectInspectionAnalysisCitations(citations)
  if (
    groupedCitations.length === 0 &&
    valueCitations.length === 0 &&
    cmmsCitations.length === 0 &&
    inspectionCitations.length === 0
  )
    return null

  return (
    <Box display='flex' wrap='wrap' gap='xs'>
      {cmmsCitations.map((citation) => (
        <ResponseSegmentCmmsChip
          key={`${citation.entry_id}:${citation.quote}`}
          citation={citation}
          onPreviewDocument={onPreviewDocument}
          onCitationHoverChange={onCitationHoverChange}
        />
      ))}
      {inspectionCitations.map((citation) => (
        <ResponseSegmentInspectionChip
          key={inspectionAnalysisCitationKey(citation)}
          citation={citation}
          onCitationHoverChange={onCitationHoverChange}
        />
      ))}
      {valueCitations.map((citation) => (
        <ResponseSegmentValueChip
          summary={timeSeriesSummaries?.get(citation.entry_id)}
          series={timeSeriesCitedSeries?.get(citation.entry_id)}
          key={citation.entry_id}
          citation={citation}
          trendRef={trendRef}
          onCitationHoverChange={onCitationHoverChange}
          onPreviewTrend={onPreviewTrend}
        />
      ))}
      {groupedCitations.map((citation) => {
        const row = resolveCitationDocument({
          citationDocumentId: citation.document_id,
          documentResults,
          pipelineConfigId: selectedPipelineConfigId,
          lookup: citationLookup,
        })
        const cantileverDocumentId = unscopeSearchDocumentIdForHydration(
          citation.document_id,
          selectedPipelineConfigId
        )
        return (
          <FadeInMount
            key={`${citation.document_id}-${citation.page_num}-${citation.failure_reason ?? 'ok'}`}>
            <ResponseSegmentCitationChip
              fallbackDocumentId={cantileverDocumentId}
              failureReason={citation.failure_reason}
              onCitationHoverChange={onCitationHoverChange}
              onPreviewDocument={onPreviewDocument}
              pageNum={citation.page_num}
              quotes={citation.quotes}
              row={row}
            />
          </FadeInMount>
        )
      })}
    </Box>
  )
}
